import { Tooltip } from '@cleanlab/design-system/components'
import { IconFolder, IconInfo } from '@cleanlab/design-system/icons'
import { cn } from '@cleanlab/design-system/utils'
import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'

import { useGetProject } from '@/api/projects'
import { APP_PATH_HELPERS, type APP_PATHS, PAGE_TITLES } from '@/global/consts'
import ENV_VARS from '@/global/environmentVariables'
import { useAdminMode } from '@/hooks/useAdminMode'
import { useUser } from '@/providers/account/UserContextProvider'

interface NavButtonProps {
  href: string
  isSelected: boolean
  label: string
}

const NavButton = ({ href, isSelected, label }: NavButtonProps) => {
  const unSelectedClassname = 'bg-transparent text-text-primary hover:bg-surface-0'

  return (
    <Link
      href={href}
      className={cn(
        'type-body-100 relative rounded-2 p-4 text-text-strong outline-0 transition-colors focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus',
        isSelected ? 'bg-surface-0-active' : unSelectedClassname
      )}
    >
      {label}
    </Link>
  )
}

const sectionTitleClassname =
  'type-overline-sm flex w-2/3 items-center gap-2 border-b border-border-1 py-2 text-text-faint'

export const ProjectNavLabel = () => {
  const params = useParams()
  const projectId = params.projectId as string
  const { data: project } = useGetProject(projectId)

  return (
    <div className="flex items-center gap-4">
      <IconFolder size={14} />
      <p className="line-clamp-1 [overflow-wrap:anywhere]">{project?.name}</p>
    </div>
  )
}

const ProjectNav = () => {
  const params = useParams()
  const { isCleanlabAdmin } = useUser()
  const { selectedOrganizationId } = useUser()
  const isVancouver = selectedOrganizationId === ENV_VARS.VANCOUVER_ORG_ID
  const projectId = params.projectId as string
  const pathname = usePathname() as (typeof APP_PATHS)[keyof typeof APP_PATHS]
  const { buildUrlWithAdminParams } = useAdminMode()

  return (
    <div className="flex flex-col gap-4">
      <div className={sectionTitleClassname}>
        <h3 className="uppercase">Monitor</h3>
        <Tooltip content="Inspect and measure your AI's overall effectiveness and safety">
          <IconInfo size={12} />
        </Tooltip>
      </div>
      <NavButton
        href={buildUrlWithAdminParams(APP_PATH_HELPERS.CREATE_PROJECT_QUERY_LOGS_PATH(projectId))}
        isSelected={pathname === APP_PATH_HELPERS.CREATE_PROJECT_QUERY_LOGS_PATH(projectId)}
        label={PAGE_TITLES.PROJECTS_QUERY_LOGS}
      />
      <NavButton
        href={buildUrlWithAdminParams(APP_PATH_HELPERS.CREATE_PROJECT_ANALYTICS_PATH(projectId))}
        isSelected={pathname === APP_PATH_HELPERS.CREATE_PROJECT_ANALYTICS_PATH(projectId)}
        label={PAGE_TITLES.PROJECTS_ANALYTICS}
      />
      <NavButton
        href={buildUrlWithAdminParams(APP_PATH_HELPERS.CREATE_PROJECT_EVALUATIONS_PATH(projectId))}
        isSelected={pathname === APP_PATH_HELPERS.CREATE_PROJECT_EVALUATIONS_PATH(projectId)}
        label={PAGE_TITLES.PROJECTS_EVALUATIONS}
      />
      {(isVancouver || isCleanlabAdmin) && (
        <NavButton
          href={buildUrlWithAdminParams(
            APP_PATH_HELPERS.CREATE_PROJECT_CLASSIFICATION_PATH(projectId)
          )}
          isSelected={pathname === APP_PATH_HELPERS.CREATE_PROJECT_CLASSIFICATION_PATH(projectId)}
          label={PAGE_TITLES.PROJECTS_CLASSIFICATION}
        />
      )}
      {isCleanlabAdmin && (
        <NavButton
          href={buildUrlWithAdminParams(APP_PATH_HELPERS.CREATE_PROJECT_ALERTS_PATH(projectId))}
          isSelected={pathname === APP_PATH_HELPERS.CREATE_PROJECT_ALERTS_PATH(projectId)}
          label={PAGE_TITLES.PROJECTS_ALERTS}
        />
      )}
      <div className={sectionTitleClassname}>
        <h3 className="uppercase">Expert in the Loop</h3>
        <Tooltip content="Improve AI responses through expert human feedback">
          <IconInfo size={12} />
        </Tooltip>
      </div>
      <NavButton
        href={buildUrlWithAdminParams(APP_PATH_HELPERS.CREATE_PROJECT_ISSUES_PATH(projectId))}
        isSelected={pathname === APP_PATH_HELPERS.CREATE_PROJECT_ISSUES_PATH(projectId)}
        label={PAGE_TITLES.PROJECTS_ISSUES}
      />
      <div className={sectionTitleClassname}>
        <h3 className="uppercase">Controls</h3>
        <Tooltip content="Block harmful AI outputs and malicious use">
          <IconInfo size={12} />
        </Tooltip>
      </div>
      <NavButton
        href={buildUrlWithAdminParams(APP_PATH_HELPERS.CREATE_PROJECT_GUARDRAILS_PATH(projectId))}
        isSelected={pathname === APP_PATH_HELPERS.CREATE_PROJECT_GUARDRAILS_PATH(projectId)}
        label={PAGE_TITLES.PROJECTS_GUARDRAILS}
      />
      <NavButton
        href={buildUrlWithAdminParams(APP_PATH_HELPERS.CREATE_PROJECT_REMEDIATIONS_PATH(projectId))}
        isSelected={pathname === APP_PATH_HELPERS.CREATE_PROJECT_REMEDIATIONS_PATH(projectId)}
        label={PAGE_TITLES.PROJECTS_REMEDIATIONS}
      />
      <div className={sectionTitleClassname}>
        <h3 className="uppercase">Admin</h3>
        <Tooltip content="Manage project settings and preferences">
          <IconInfo size={12} />
        </Tooltip>
      </div>
      <NavButton
        href={buildUrlWithAdminParams(APP_PATH_HELPERS.CREATE_PROJECT_SETTINGS_PATH(projectId))}
        isSelected={pathname === APP_PATH_HELPERS.CREATE_PROJECT_SETTINGS_PATH(projectId)}
        label={PAGE_TITLES.PROJECTS_SETTINGS}
      />
    </div>
  )
}

export default ProjectNav
